import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { Customer, searchQueryParams } from "../helpers/shared-interfaces";
import { Col, Row, Tag, message } from "antd";
import Card from "../components/Card";
import Table, { ColumnType } from "antd/lib/table";
import debounce from "lodash.debounce";
import { request , showTotalPagination } from "../helpers/utilities";
import { PageToolbar } from "../components/PageToolbar";
import * as Sentry from "@sentry/browser";
import { GET_ARCHIVED_CUSTOMERS } from "../gql/customers";
import queryString from "query-string";
import { extractPageFromQuery } from "../components/TableFilters";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface State {
  isLoading: boolean;
  page: number;
  pageSize: number;
  q: string;
  customers: Customer[];
  customersCount: number;
}

function getCustomerColumns<T extends Customer = Customer>(
  customers: Customer[],
): ColumnType<T>[] {
  return [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "2%",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "PetraID",
      dataIndex: "petraId",
      key: "petraId",
      width: "10%",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Name",
      dataIndex: "company",
      key: "company",
      width: "13%",
      render(text: string, record: Customer): JSX.Element {
        return <span>{record.fullname}</span>;
      },
    },
    {
      title: "SSNIT",
      dataIndex: "ssnitNumber",
      key: "ssnitNumber",
      width: "10%",
      render(text: string): JSX.Element {
        if (text) return <span>{text}</span>;
        return (
          <Tag color="red" key="ssnit">
            Not Assigned
          </Tag>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "customerEmails",
      key: "customerEmails",
      width: "20%",
      render(text: string, record: Customer): JSX.Element {
        const email = record?.customerEmails?.[0]?.email ?? ""
        return (
          <span>
            {email ? (
              <div key="cEmail">
                <span>{email.address}</span>
              </div>
            ) : (
              <Tag color="red" key="cEmailNotAssigned">
                No Email
              </Tag>
            )}
          </span>
        );
      },
    },

    {
      title: "Phone",
      dataIndex: "activeCustomerPhones",
      key: "activeCustomerPhones",
      width: "15%",
      render(text: string, record: Customer): JSX.Element {
        const phone = record?.activeCustomerPhones?.[0]?.phone ?? "";
        return (
          <span>
            {phone ? (
              <div key="cPhone">
                <span>{phone.number}</span>
              </div>
            ) : (
              <Tag color="red" key="cPhoneNotAssigned">
                No Mobile Number
              </Tag>
            )}
          </span>
        );
      },
    },
  ];
}

export default class ArchivedCustomers extends React.Component<
  RouteComponentProps,
  State
> {
  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      customers: [],
      customersCount: 0,
      page: 1,
      pageSize: 25,
      q: "",
    };

    this.nextPage = this.nextPage.bind(this);
  }

  async componentDidMount(): Promise<void> {
    this.mergeWithLocalState();
    await this.triggerImmediateSearch();
  }

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState(
      {
        isLoading: true,
        page,
        pageSize,
      },
      async () => {
        await this.triggerImmediateSearch();
      },
    );
  }

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.setState({ q: event.target.value, page: 1 });
    await this.triggerDelayedSearch();
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  triggerImmediateSearch = async (): Promise<void> => {
    const { q, page, pageSize } = this.state;

    this.setState({ isLoading: true });

    try {
      const { data: { data } }: any = await request({
        query: GET_ARCHIVED_CUSTOMERS,
        variables: { page, q, numberPerPage: pageSize },
      });
      
      this.syncBrowserQueryString();
      this.setState({
        customers: data?.archivedCustomerSearch?.results,
        customersCount: data?.archivedCustomerSearch?.count,
      });
    } catch (error) {
      message.error(`Failed To Load Customers - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  extractQueryParamsFromUrl = (): searchQueryParams => {
    const parsed = queryString.parse(window.location.search);
    return JSON.parse(JSON.stringify(parsed)) as searchQueryParams;
  };

  mergeWithLocalState = async (): Promise<void> => {
    const parsed = this.extractQueryParamsFromUrl();
    let q = "";
    let page = 1;

    if (parsed.q) {
      q = parsed.q;
    }

    if (parsed.page) {
      page = extractPageFromQuery(parsed.page);
    }

    this.setState({
      q,
      page,
    });
  };

  syncBrowserQueryString = (): void => {
    const { q, page } = this.state;
    const queryStrings = [];
    if (q) {
      queryStrings.push(`q=${q}`);
    }
    queryStrings.push(`page=${page}`);

    const queryString = `?${queryStrings.join("&")}`;

    this.props.history.replace(queryString);
  };

  render(): JSX.Element | null {
    const { isLoading, q, customers, customersCount, page, pageSize } =
      this.state;

    const resultsWithKeys: Customer[] = customers.map(
      (result: Customer): Customer => {
        return {
          ...result,
          key: result.id,
        };
      },
    );

    return (
        <Card>
          <div className="customers-container">
            <PageToolbar
              searchPlaceholder="Search by Email, Phone, SSNIT, First name, Middle name, Last name or PetraID ..."
              searchValue={q}
              onSearchChange={this.handleSearchInput}
              totalCount={customersCount}
              currentDataCount={customers.length}
              page={page}
              pageSize={pageSize}
              modelName="archived customers"
            />

            <Row>
              <Col span={24}>
                <Table
                  loading={isLoading}
                  scroll={{ x: 400 }}
                    columns={getCustomerColumns(customers)}
                    dataSource={resultsWithKeys}
                    onRow={(record) => ({
                      onClick: () => {
                        this.props.history.push(
                          {
                            pathname: `/archived-customers/${record.id}`,
                            state: { type: "customer", customers },
                          }
                        )
                      },
                      style: { cursor: 'pointer' }

                    })}
                    pagination={{
                      current: page,
                      defaultPageSize: pageSize,
                      total: customersCount,
                      onChange: this.nextPage,
                      showTotal: showTotalPagination,
                    }}
                  />
              </Col>
            </Row>
          </div>
        </Card>
    );
  }
}
